import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ContactsByUserService, addContactAvatarService, addContactService, contactRechargesByUserService, deleteContactService, updateContactService } from './ContactsService';
import useAuthContext from "../../hooks/useAuthContext";
import { getTotalPages } from "../../utils/common";
import { useToast } from "@chakra-ui/toast";

const itemsPerPage = 10;

const queryContacts = () => {
  const { user, isAuthenticated } = useAuthContext();
  const toast = useToast();
  const queryClient = useQueryClient();
  
  const contactsRechargesByUser = ({ search = '', status = null } = {}) => {
    const { data, isLoading, isError, error, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
      ['contactsRechargesByUser', { search, status }],
      ({ pageParam = 1 }) => contactRechargesByUserService({
        queryKey: ['contacts-by-user',
        { token: user?.access_token, page: pageParam, search, status }]
      }),
      {
        getNextPageParam: (lastPage) => {
          const nextPage = lastPage.data.data.meta.current_page + 1;
          const totalPages = getTotalPages(lastPage.data.data.meta.total, itemsPerPage)
          return nextPage <= totalPages ? nextPage : undefined;
        },
        keepPreviousData: true,
      }
    );

    // concat all pages
    const allPages = data?.pages.reduce((acc, page) => [...acc, ...page.data.data.data], []);
    return { data: allPages, isLoading, isFetching, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage };
  }

  const getContactList = () => {
    const { data, isLoading, isError, error } = useQuery(
      ['contacts-by-user'], () => ContactsByUserService({ token: user?.access_token }), {
        enabled: isAuthenticated,
        staleTime: 1000 * 60 * 5, // 5 minutes
      }
    );

    const dataResult = data?.data?.data || [];

    return { data: dataResult, isLoading, isError, error };
  }
  
  const addContact = () => {
    const { mutate, isLoading, error, isSuccess, ...rest } = useMutation((opt) => addContactService({
      contact: {
        ...opt,
        avatar: undefined
      },
      token: user?.access_token,
    }), {
      onSuccess: (data, opt) => {
        toast({
          title: 'Contacto agregado',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });

        const oldCache = queryClient.getQueryData(['contacts-by-user']);
        if (!oldCache) return;
        const dataResponse = [...oldCache.data.data, data.data.data];
        const newCache = {
          ...oldCache,
          data: {
            ...oldCache.data,
            data: dataResponse,
          }
        };
        
        queryClient.setQueryData(['contacts-by-user'], newCache);
        queryClient.invalidateQueries(['contacts-by-user']);

        if (opt?.avatar) {
          const formData = new FormData();
          formData.append('avatar', opt.avatar);
          formData.append('id', data.data.data.id);
          addContactAvatarService({
            contact: formData,
            token: user?.access_token,
          })
            .then(() => {
              toast({
                title: 'Avatar de usuario establecido',
                status: 'success',
                duration: 2000,
                isClosable: true,
                position: 'top-right',
              });

              queryClient.invalidateQueries(['contacts-by-user']);
            });
        }
      },
      onError: (error) => {
        toast({
          title: 'Error al agregar el contacto',
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
        console.error('Adding contact:', error);
      }
    });

    return { mutate, isLoading, error, isSuccess, ...rest };
  }

  const addContactAvatar = () => {
    const { mutate, isLoading, error, isSuccess, ...rest } = useMutation((opt) => addContactAvatarService({
      contact: {
        ...opt,
        avatar: undefined
      },
      token: user?.access_token,
    }), {
      onSuccess: (data) => {
        toast({
          title: 'Avatar de usuario establecido',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });

        const oldCache = queryClient.getQueryData(['contacts-by-user']);
        if (!oldCache) return;
        const dataResponse = [...oldCache.data.data, data.data.data];
        const newCache = {
          ...oldCache,
          data: {
            ...oldCache.data,
            data: dataResponse,
          }
        };
        queryClient.setQueryData(['contacts-by-user'], newCache);
        
        queryClient.invalidateQueries(['contacts-by-user']);
      },
      onError: (error) => {
        toast({
          title: 'Error al actualizar el avatar del contacto',
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
        console.error('Adding contact:', error);
      }
    });

    return { mutate, isLoading, error, isSuccess, ...rest };
  }

  const updateContact = () => {
    const { mutate, isLoading, error, isSuccess } = useMutation((opt) => {
        const favorite = opt.favorite;

        const oldCache = queryClient.getQueryData(['contacts-by-user']);
        if (oldCache) {
          const dataFiltered = oldCache.data.data.map((contact) => {
            if (contact.id === Number(opt.id)) {
              return {
                ...contact,
                favorite: Boolean(Number(favorite)), // parse "1" to true and "0" to false
              };
            }
            return contact;
          });
          const newCache = {
            ...oldCache,
            data: {
              ...oldCache.data,
              data: dataFiltered,
            }
          };
          queryClient.setQueryData(['contacts-by-user'], newCache);
        }
      
        return updateContactService({
        contact: opt, // opt is a formData
        token: user?.access_token,
      })
    }, {
      onSuccess: (data, opt) => {
        const oldCache = queryClient.getQueryData(['contacts-by-user']);
        if (!oldCache) return;
        const dataFiltered = oldCache.data.data.map((contact) => {
          if (contact.id === Number(opt.id)) {
            return data.data.data;
          }
          return contact;
        });
        const newCache = {
          ...oldCache,
          data: {
            ...oldCache.data,
            data: dataFiltered,
          }
        };
        queryClient.setQueryData(['contacts-by-user'], newCache);
        // queryClient.invalidateQueries(['contacts-by-user']);
        
        if (opt.favorite) return; // dont show toast if favorite is updated
        toast({
          title: 'Contacto actualizado',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });

        if (opt?.avatar) {
          const formData = new FormData();
          formData.append('avatar', opt.avatar);
          formData.append('id', opt.id);
          addContactAvatarService({
            contact: formData,
            token: user?.access_token,
          })
            .then(() => {
              toast({
                title: 'Avatar de usuario actualizado',
                status: 'success',
                duration: 2000,
                isClosable: true,
                position: 'top-right',
              });

              queryClient.invalidateQueries(['contacts-by-user']);
            });
        }
      },
      onError: (error) => {
        toast({
          title: 'Error al actualizar el contacto',
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
        console.error('Updating contact:', error);
      }
    });

    return { mutate, isLoading, error, isSuccess };
  }

  const deleteContact = () => {
    const { mutate, isLoading, error, isSuccess } = useMutation((opt) => deleteContactService({
      id: opt,
      token: user?.access_token,
    }), {
      onSuccess: (data, opt) => {
        toast({
          title: 'Contacto eliminado',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });

        const oldCache = queryClient.getQueryData(['contacts-by-user']);
        if (!oldCache) return;
        const dataFiltered = oldCache.data.data.filter((contact) => contact.id !== opt);
        const newCache = {
          ...oldCache,
          data: {
            ...oldCache.data,
            data: dataFiltered,
          }
        };
        queryClient.setQueryData(['contacts-by-user'], newCache);
        
        queryClient.invalidateQueries(['contacts-by-user']);
      },
      onError: (error) => {
        toast({
          title: 'Error al eliminar el contacto',
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
        console.error('Deleting contact:', error);
      }
    });

    return { mutate, isLoading, error, isSuccess };
  }
  
  return { contactsRechargesByUser, getContactList, addContact, addContactAvatar, updateContact, deleteContact };
}

export default queryContacts;